import { useEffect, useState } from "react";
import { FileText, Trash2, Printer, Check, X as XIcon } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent } from "@/components/ui/dialog";
import Pager from "@/components/site/Pager";
import { api } from "@/lib/api";
import { toast } from "sonner";

const STATUSES = ["draft", "sent", "paid", "overdue"];
const STATUS_COLORS = {
  draft: "bg-slate-100 text-slate-700",
  sent: "bg-blue-100 text-blue-700",
  paid: "bg-emerald-100 text-emerald-700",
  overdue: "bg-rose-100 text-rose-700",
};
const PAGE_SIZE = 20;

const inr = (n) => `₹${(n || 0).toLocaleString("en-IN", { minimumFractionDigits: 2 })}`;

export default function AdminInvoices() {
  const [items, setItems] = useState([]);
  const [total, setTotal] = useState(0);
  const [totalPaid, setTotalPaid] = useState(0);
  const [totalOutstanding, setTotalOutstanding] = useState(0);
  const [page, setPage] = useState(1);
  const [statusF, setStatusF] = useState("");
  const [preview, setPreview] = useState(null);

  const load = () => api.get("/admin/invoices", { params: { status: statusF || undefined, page, page_size: PAGE_SIZE } }).then((r) => {
    setItems(r.data.items || []);
    setTotal(Number(r.data.total) || 0);
    setTotalPaid(Number(r.data.total_paid) || 0);
    setTotalOutstanding(Number(r.data.total_outstanding) || 0);
  });
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [statusF, page]);
  useEffect(() => { setPage(1); }, [statusF]);

  const updateStatus = async (id, status) => {
    await api.put(`/admin/invoices/${id}`, { status });
    toast.success("Updated"); load();
  };

  const remove = async (id) => {
    if (!window.confirm("Delete this invoice?")) return;
    await api.delete(`/admin/invoices/${id}`); load();
  };

  const open = async (id) => {
    const r = await api.get(`/admin/invoices/${id}`);
    setPreview(r.data);
  };

  return (
    <div className="space-y-6" data-testid="admin-invoices">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="font-heading text-2xl font-semibold text-slate-900">Invoices</h1>
          <p className="text-sm text-slate-500">Placement fee invoicing</p>
        </div>
      </div>

      <div className="grid gap-4 md:grid-cols-3">
        <div className="rounded-xl border border-slate-200 bg-white p-5"><div className="text-xs uppercase tracking-widest text-slate-500">Total invoices</div><div className="mt-2 font-heading text-3xl font-bold text-slate-900">{total}</div></div>
        <div className="rounded-xl border border-emerald-200 bg-emerald-50/50 p-5"><div className="text-xs uppercase tracking-widest text-emerald-700"><Check className="mr-1 inline h-3 w-3" />Paid</div><div className="mt-2 font-heading text-3xl font-bold text-emerald-700">{inr(totalPaid)}</div></div>
        <div className="rounded-xl border border-amber-200 bg-amber-50/50 p-5"><div className="text-xs uppercase tracking-widest text-amber-700">Outstanding</div><div className="mt-2 font-heading text-3xl font-bold text-amber-700">{inr(totalOutstanding)}</div></div>
      </div>

      <div className="flex items-center gap-2">
        <Select value={statusF || "all"} onValueChange={(v) => setStatusF(v === "all" ? "" : v)}>
          <SelectTrigger className="w-48"><SelectValue placeholder="Status" /></SelectTrigger>
          <SelectContent><SelectItem value="all">All</SelectItem>{STATUSES.map((s) => <SelectItem key={s} value={s}>{s}</SelectItem>)}</SelectContent>
        </Select>
      </div>

      <div className="overflow-hidden rounded-xl border border-slate-200 bg-white">
        <table className="w-full text-sm">
          <thead className="border-b bg-slate-50 text-left text-xs uppercase tracking-wider text-slate-500">
            <tr><th className="px-4 py-3">Invoice #</th><th className="px-4 py-3">Client</th><th className="px-4 py-3">Candidate / Role</th><th className="px-4 py-3">Amount</th><th className="px-4 py-3">Status</th><th className="px-4 py-3 text-right">Actions</th></tr>
          </thead>
          <tbody>
            {items.map((i) => (
              <tr key={i.id} className="border-b last:border-0 hover:bg-slate-50" data-testid={`invoice-${i.id}`}>
                <td className="px-4 py-3 font-semibold text-slate-900">{i.invoice_number}</td>
                <td className="px-4 py-3">{i.client_name}</td>
                <td className="px-4 py-3"><div className="text-slate-800">{i.candidate_name}</div><div className="text-xs text-slate-500">{i.job_title}</div></td>
                <td className="px-4 py-3 font-semibold text-slate-900">{inr(i.total_amount)}<div className="text-xs font-normal text-slate-500">{inr(i.base_amount)} + GST</div></td>
                <td className="px-4 py-3">
                  <Select value={i.status} onValueChange={(v) => updateStatus(i.id, v)}>
                    <SelectTrigger className={`h-8 w-32 ${STATUS_COLORS[i.status] || ""}`}><SelectValue /></SelectTrigger>
                    <SelectContent>{STATUSES.map((s) => <SelectItem key={s} value={s}>{s}</SelectItem>)}</SelectContent>
                  </Select>
                </td>
                <td className="px-4 py-3 text-right">
                  <Button variant="ghost" size="sm" onClick={() => open(i.id)} data-testid={`invoice-open-${i.id}`}><FileText className="h-3.5 w-3.5" /></Button>
                  <Button variant="ghost" size="sm" onClick={() => remove(i.id)}><Trash2 className="h-3.5 w-3.5 text-rose-600" /></Button>
                </td>
              </tr>
            ))}
            {items.length === 0 && <tr><td colSpan={6} className="px-4 py-10 text-center text-slate-400">No invoices yet. Raise one from a Placement.</td></tr>}
          </tbody>
        </table>
      </div>

      <Pager page={page} pageSize={PAGE_SIZE} total={total} onChange={setPage} testIdPrefix="admin-invoices-pager" />

      {preview && <InvoicePreview inv={preview} onClose={() => setPreview(null)} />}
    </div>
  );
}

function InvoicePreview({ inv, onClose }) {
  const s = inv._settings || {};
  return (
    <Dialog open={true} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-w-3xl">
        <div className="flex items-center justify-between print:hidden">
          <div className="text-sm text-slate-500">Preview · Save as PDF via browser Print</div>
          <div className="flex gap-2">
            <Button variant="outline" size="sm" onClick={() => window.print()}><Printer className="mr-1 h-3.5 w-3.5" />Print / Save PDF</Button>
            <Button variant="ghost" size="sm" onClick={onClose}><XIcon className="h-3.5 w-3.5" /></Button>
          </div>
        </div>
        <div id="invoice-print" className="mt-4 rounded-xl border border-slate-200 bg-white p-8 text-sm text-slate-800">
          <div className="mb-6 flex items-start justify-between">
            <div>
              <div className="font-heading text-2xl font-bold text-primary">{s.brand || "PharmacoHires"}</div>
              <div className="mt-1 whitespace-pre-line text-xs text-slate-500">{s.company_address}</div>
              <div className="text-xs text-slate-500">GSTIN: {s.company_gstin || "—"}</div>
              <div className="text-xs text-slate-500">{s.contact_email} · {s.contact_phone}</div>
            </div>
            <div className="text-right">
              <div className="font-heading text-xl font-semibold uppercase tracking-widest text-slate-900">Invoice</div>
              <div className="mt-1 text-sm font-semibold text-slate-900">{inv.invoice_number}</div>
              <div className="text-xs text-slate-500">Date: {inv.invoice_date}</div>
              <Badge className="mt-2 uppercase">{inv.status}</Badge>
            </div>
          </div>

          <div className="mb-6 grid gap-4 sm:grid-cols-2">
            <div>
              <div className="text-xs font-semibold uppercase tracking-widest text-slate-500">Bill To</div>
              <div className="mt-1 font-semibold text-slate-900">{inv.client_name}</div>
              <div className="text-xs text-slate-500 whitespace-pre-line">{inv.client_address}</div>
              <div className="text-xs text-slate-500">GSTIN: {inv.client_gstin || "—"}</div>
            </div>
            <div>
              <div className="text-xs font-semibold uppercase tracking-widest text-slate-500">Placement</div>
              <div className="mt-1 text-slate-800">Candidate: <span className="font-semibold">{inv.candidate_name}</span></div>
              <div className="text-slate-800">Role: {inv.job_title}</div>
              <div className="text-slate-800">Offered CTC: ₹{inv.ctc_offered?.toLocaleString("en-IN")}</div>
            </div>
          </div>

          <table className="w-full border-collapse text-sm">
            <thead className="bg-slate-50 text-left text-xs uppercase tracking-widest text-slate-500">
              <tr><th className="border border-slate-200 px-4 py-2">Description</th><th className="border border-slate-200 px-4 py-2 text-right">Amount</th></tr>
            </thead>
            <tbody>
              <tr><td className="border border-slate-200 px-4 py-3">Placement fee ({inv.fee_percent}% of CTC ₹{inv.ctc_offered?.toLocaleString("en-IN")})</td><td className="border border-slate-200 px-4 py-3 text-right">₹{inv.base_amount?.toLocaleString("en-IN")}</td></tr>
              <tr><td className="border border-slate-200 px-4 py-3">GST @ {inv.gst_percent}%</td><td className="border border-slate-200 px-4 py-3 text-right">₹{inv.gst_amount?.toLocaleString("en-IN")}</td></tr>
              <tr className="bg-primary/5"><td className="border border-slate-200 px-4 py-3 font-semibold">Total Payable</td><td className="border border-slate-200 px-4 py-3 text-right font-heading text-lg font-bold text-primary">₹{inv.total_amount?.toLocaleString("en-IN")}</td></tr>
            </tbody>
          </table>

          {s.company_bank && (
            <div className="mt-6 rounded-lg bg-slate-50 p-4">
              <div className="text-xs font-semibold uppercase tracking-widest text-slate-500">Bank details</div>
              <div className="mt-1 whitespace-pre-line text-slate-800">{s.company_bank}</div>
            </div>
          )}
          <div className="mt-6 text-xs text-slate-500">This is a system-generated invoice. Please share the transaction reference upon payment.</div>
        </div>
      </DialogContent>
    </Dialog>
  );
}
